/* import __COLOCATED_TEMPLATE__ from './predicates-bridge.hbs'; */
/* RESPONSIBLE TEAM: team-fin-qa */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';
import { registerDestructor } from '@ember/destroyable';
import { action } from '@ember/object';
import PredicateGroup from 'predicates/models/predicate-group';
import { get } from 'embercom/lib/ajax';

interface Signature {
  Args: {};
}

export default class PredicatesBridge extends Component<Signature> {
  @service declare store: $TSFixMe;
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;
  @service declare appService: $TSFixMe;
  @service declare attributeService: $TSFixMe;

  // Descriptor IDs that are string on the backend but rendered as lists with dynamic options
  static DYNAMIC_LIST_DESCRIPTOR_IDS = new Set([
    'ai_insights_topic',
    'ai_insights_subtopic_ids',
    'customer_experience_attributes',
  ]);

  @tracked showPredicatesEditorSideSheet = false;
  @tracked predicateGroup?: $TSFixMe;
  @tracked finInvolvedPredicates: unknown[] = [];
  @tracked dynamicListOverrides: Map<string, $TSFixMe> = new Map();

  constructor(owner: unknown, args: {}) {
    super(owner, args);

    let unsubscribeOpen = this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
      'fin-qa-monitor:predicates:open',
      async (data) => {
        let eventData = data as {
          predicates?: unknown[];
        };

        // Clear predicateGroup to show loading state and open the side sheet immediately
        this.predicateGroup = undefined;
        this.showPredicatesEditorSideSheet = true;

        // Reload attributes and rule context data from the server
        try {
          await Promise.all([
            this.store.findAll('conversation-attributes/descriptor'),
            this.appService.app.reload(),
            // Load guidance + escalation rule data so the native guidanceAppliedOptions
            // dropdown in the predicates editor can resolve options via peekAll
            this.store.findAll('ai-agent/guidelines'),
            this.store.findAll('ai-agent/escalation-rule'),
          ]);
        } catch (e: unknown) {
          // Close the side sheet if data loading fails. No error modal shown as these
          // are background data fetches for the predicates editor (descriptors, guidelines, escalation rules).
          this.showPredicatesEditorSideSheet = false;
          return;
        }

        // Fetch dynamic list options for descriptors that need runtime-fetched dropdown values
        let dynamicOptions = await this.fetchDynamicListOptions();

        // Build override attributes for descriptors that need dynamic list options.
        // These replace the base attributes (which have type 'string') with list-type
        // proxies so the predicates editor renders a dropdown.
        let overrides = new Map<string, $TSFixMe>();
        for (let descriptorId of PredicatesBridge.DYNAMIC_LIST_DESCRIPTOR_IDS) {
          let descriptor = this.store.peekRecord(
            'conversation-attributes/descriptor',
            descriptorId,
          );
          let options = dynamicOptions.get(descriptorId);
          if (descriptor && options) {
            // Build a plain object proxy instead of mutating the live Ember Data store record.
            // peekRecord returns the shared store singleton — mutating it causes duplicate
            // list options to accumulate on every editor open.
            let descriptorProxy = {
              ...descriptor.getProperties(Object.keys(descriptor.toJSON())),
              id: descriptor.id,
              dataType: 'list',
              listOptions: options.map(
                ({ identifier, value }: { identifier: string; value: string }) => ({
                  listOptionId: identifier,
                  label: value,
                  archived: false,
                }),
              ),
            };
            let attribute =
              this.attributeService._conversationAttributeDescriptorToAttribute(descriptorProxy);
            overrides.set(attribute.identifier, attribute);
          }
        }

        this.dynamicListOverrides = overrides;

        let allPredicates: unknown[] = eventData.predicates || [];

        this.finInvolvedPredicates = allPredicates.filter(
          (p: $TSFixMe) => p.attribute === 'conversation.fin_involved',
        );
        let editablePredicates = allPredicates
          .filter((p: $TSFixMe) => p.attribute !== 'conversation.fin_involved')
          .map((p: $TSFixMe) => this.normalizePredicateType(p));

        this.predicateGroup = this.store.createFragment('predicates/predicate-group', {
          predicates: PredicateGroup.convertRawPredicates(this.store, editablePredicates),
        });
      },
    );

    let unsubscribeClose = this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
      'fin-qa-monitor:predicates:close',
      () => {
        this.showPredicatesEditorSideSheet = false;
      },
    );

    registerDestructor(this, () => {
      unsubscribeOpen();
      unsubscribeClose();
    });
  }

  async fetchDynamicListOptions(): Promise<Map<string, { identifier: string; value: string }[]>> {
    let optionsMap = new Map<string, { identifier: string; value: string }[]>();
    let appId = this.appService.app.id;

    let [topicsResult, cxReasonsResult] = await Promise.allSettled([
      get(`/ember/ai_insights/topics`, { app_id: appId }),
      get(`/teammate_app/qa/monitors/cx_score_reasons`, { app_id: appId }),
    ]);

    if (topicsResult.status === 'fulfilled') {
      let topics = topicsResult.value?.topics || [];
      let uniqueTopicNames = [...new Set(topics.map((t: $TSFixMe) => t.topicName))] as string[];
      optionsMap.set(
        'ai_insights_topic',
        uniqueTopicNames.map((name) => ({ identifier: name, value: name })),
      );
      optionsMap.set(
        'ai_insights_subtopic_ids',
        topics.map((t: $TSFixMe) => ({
          identifier: String(t.subtopicId),
          value: t.subtopicName,
        })),
      );
    }

    if (cxReasonsResult.status === 'fulfilled') {
      let cxReasons =
        cxReasonsResult.value?.cx_score_reasons || cxReasonsResult.value?.cxScoreReasons || [];
      optionsMap.set(
        'customer_experience_attributes',
        cxReasons.map((r: $TSFixMe) => ({ identifier: r.id, value: r.label })),
      );
    }

    return optionsMap;
  }

  // Predicate types from the server may not match the descriptor's dataType.
  // Normalize them so the predicates editor renders the correct editor component.
  normalizePredicateType(predicate: $TSFixMe): $TSFixMe {
    // Server sends latest_csat_rating as type 'string', but we need 'latest_csat_rating'
    // so the predicates editor renders the optionalSelect dropdown with rating options
    if (predicate.attribute === 'conversation.latest_csat_rating') {
      return { ...predicate, type: 'latest_csat_rating' };
    }

    if (!predicate.attribute?.startsWith('conversation.custom_attribute.')) {
      return predicate;
    }

    let descriptorId = predicate.attribute.replace('conversation.custom_attribute.', '');

    // Attributes with dynamically-fetched list options need conversation_attribute_list type
    if (PredicatesBridge.DYNAMIC_LIST_DESCRIPTOR_IDS.has(descriptorId)) {
      return { ...predicate, type: 'conversation_attribute_list' };
    }

    let descriptor = this.store.peekRecord('conversation-attributes/descriptor', descriptorId);
    if (descriptor && descriptor.dataType !== predicate.type) {
      return { ...predicate, type: descriptor.dataType };
    }

    return predicate;
  }

  @action closePredicatesEditorSideSheet() {
    this.showPredicatesEditorSideSheet = false;
    this.reactTeammateAppIsland.dispatchBridgeEvent('teammate-app:event', {
      identifier: 'fin-qa-monitor:predicates:closed',
      data: {},
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'FinQaMonitor::PredicatesBridge': typeof PredicatesBridge;
    'fin-qa-monitor/predicates-bridge': typeof PredicatesBridge;
  }
}
